import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import GoogleIcon from "@mui/icons-material/Google";
import AppleIcon from "@mui/icons-material/Apple";

import useStorefrontSignIn from "../../shared/auth/useStorefrontSignIn";
import EdsLogo from "../components/EdsLogo";
import { EDS, btnPrimary, resetButton } from "../theme";

function MicrosoftMark() {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 7px)", gap: "2px" }}>
      <Box sx={{ width: 7, height: 7, bgcolor: "#F25022" }} />
      <Box sx={{ width: 7, height: 7, bgcolor: "#7FBA00" }} />
      <Box sx={{ width: 7, height: 7, bgcolor: "#00A4EF" }} />
      <Box sx={{ width: 7, height: 7, bgcolor: "#FFB900" }} />
    </Box>
  );
}

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    fontSize: "0.9375rem",
    bgcolor: "#fff",
    "& fieldset": { borderColor: EDS.lineMedium },
    "&:hover fieldset": { borderColor: EDS.subtle },
    "&.Mui-focused fieldset": { borderColor: EDS.ink, borderWidth: 1 },
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.875rem",
    color: EDS.muted,
    "&.Mui-focused": { color: EDS.ink },
  },
};

const socialBtn = {
  ...resetButton,
  width: 44,
  height: 44,
  borderRadius: "999px",
  display: "grid",
  placeItems: "center",
  border: `1px solid ${EDS.lineMedium}`,
  bgcolor: "#fff",
  transition: "border-color 160ms ease, opacity 160ms ease",
  "&:hover": { borderColor: EDS.ink, opacity: 0.9 },
  "&:disabled": { opacity: 0.45, cursor: "not-allowed" },
};

const authCardSx = {
  width: "100%",
  maxWidth: 420,
  bgcolor: EDS.surface,
  borderRadius: "16px",
  boxShadow: "0 12px 40px rgba(15, 23, 42, 0.06)",
  px: { xs: 3, sm: 4 },
  py: { xs: 4, sm: 4.5 },
};

export default function SignInPage() {
  const {
    email,
    setEmail,
    password,
    setPassword,
    showPassword,
    setShowPassword,
    loading,
    error,
    handleEmailLogin,
    handleSocialSignIn,
    isMsalPopupCallback,
    googleClientId,
    appleClientId,
    microsoftClientId,
    registerPath,
  } = useStorefrontSignIn({ mode: "portal" });

  if (isMsalPopupCallback) {
    return (
      <Box
        sx={{
          minHeight: "100dvh",
          display: "grid",
          placeItems: "center",
          bgcolor: EDS.canvas,
          px: 2,
        }}
      >
        <Box sx={{ textAlign: "center", color: EDS.muted, fontSize: "0.875rem" }}>
          Completing Microsoft sign in…
        </Box>
      </Box>
    );
  }

  const hasSocial = googleClientId || appleClientId || microsoftClientId;

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        bgcolor: EDS.canvas,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: { xs: 4, md: 6 },
        fontFamily: EDS.fontDisplay,
        WebkitFontSmoothing: "antialiased",
      }}
    >
      <Box sx={authCardSx}>
        <Box sx={{ textAlign: "center", mb: 3.5 }}>
          <Link to="/" style={{ textDecoration: "none", display: "inline-block" }}>
            <EdsLogo variant="signIn" centered />
          </Link>
        </Box>

        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Box
            component="h1"
            sx={{
              m: 0,
              fontSize: "1.5rem",
              fontWeight: 500,
              letterSpacing: "-0.03em",
              color: EDS.ink,
            }}
          >
            Sign in
          </Box>
          <Box
            component="p"
            sx={{
              mt: 0.75,
              mb: 0,
              fontSize: "0.9375rem",
              color: EDS.muted,
              fontWeight: 400,
              lineHeight: 1.6,
            }}
          >
            Access your account to order parts and track shipments.
          </Box>
        </Box>

        {error ? (
          <Box
            component="p"
            sx={{
              mb: 2,
              fontSize: "0.875rem",
              color: "#B42318",
              fontWeight: 400,
              textAlign: "center",
            }}
          >
            {error}
          </Box>
        ) : null}

        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            handleEmailLogin();
          }}
        >
          <Box sx={{ display: "grid", gap: 2 }}>
            <TextField
              type="email"
              label="Email"
              fullWidth
              autoComplete="email"
              value={email}
              disabled={loading}
              onChange={(e) => setEmail(e.target.value)}
              sx={fieldSx}
            />
            <TextField
              type={showPassword ? "text" : "password"}
              label="Password"
              fullWidth
              autoComplete="current-password"
              value={password}
              disabled={loading}
              onChange={(e) => setPassword(e.target.value)}
              sx={fieldSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      onClick={() => setShowPassword((v) => !v)}
                      sx={{ color: EDS.subtle }}
                    >
                      {showPassword ? (
                        <VisibilityOffOutlinedIcon sx={{ fontSize: 20 }} />
                      ) : (
                        <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box
            component="button"
            type="submit"
            disabled={loading}
            sx={{
              ...btnPrimary,
              width: "100%",
              mt: 2.5,
              py: 1.25,
              opacity: loading ? 0.6 : 1,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Signing in…" : "Sign in"}
          </Box>
        </Box>

        {hasSocial ? (
          <Box sx={{ mt: 3 }}>
            <Box
              component="p"
              sx={{
                m: "0 0 1rem",
                fontSize: "0.8125rem",
                color: EDS.subtle,
                textAlign: "center",
                fontWeight: 400,
              }}
            >
              or continue with
            </Box>
            <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
              {googleClientId ? (
                <Box
                  component="button"
                  type="button"
                  disabled={loading}
                  onClick={() => handleSocialSignIn("google")}
                  sx={socialBtn}
                  aria-label="Continue with Google"
                >
                  <GoogleIcon sx={{ fontSize: 20, color: "#4285F4" }} />
                </Box>
              ) : null}
              {appleClientId ? (
                <Box
                  component="button"
                  type="button"
                  disabled={loading}
                  onClick={() => handleSocialSignIn("apple")}
                  sx={socialBtn}
                  aria-label="Continue with Apple"
                >
                  <AppleIcon sx={{ fontSize: 20, color: EDS.ink }} />
                </Box>
              ) : null}
              {microsoftClientId ? (
                <Box
                  component="button"
                  type="button"
                  disabled={loading}
                  onClick={() => handleSocialSignIn("microsoft")}
                  sx={socialBtn}
                  aria-label="Continue with Microsoft"
                >
                  <MicrosoftMark />
                </Box>
              ) : null}
            </Box>
          </Box>
        ) : null}

        <Box
          component="p"
          sx={{
            mt: 3,
            mb: 0,
            fontSize: "0.875rem",
            color: EDS.muted,
            fontWeight: 400,
            textAlign: "center",
          }}
        >
          No account?{" "}
          <Link
            to={registerPath}
            style={{
              color: EDS.link,
              fontWeight: 500,
              textDecoration: "none",
            }}
          >
            Create one
          </Link>
        </Box>
      </Box>
    </Box>
  );
}
